import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Repeat } from 'lucide-react';
import { isFilterMultiSelectNone } from '../filterMultiSelectUtils';
import { employeeDetailPathFromPrefix } from '../../utils/employeeRoutes';
import { leadDetailPath } from '../../utils/leadRoutes';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { absenceBarStyle } from '../../utils/absenceBarColors';
import { activityTypeBarStyle } from '../../utils/activityTypeColors';
import {
  formatBlockAbsenceDateLabel,
  formatBlockScheduleLabel,
  canResizeActivityBlock,
} from './plantafelBlockTime';
import {
  blockStackPositionStyle,
  computeRowStackLayout,
  type BlockStackLayout,
} from './plantafelBlockLanes';
import {
  PlantafelDisplayContext,
  PlantafelTimelineRowRefContext,
  usePlantafelDisplay,
} from './PlantafelTimelineContext';
import { PLANTAFEL_WEEKDAY_LABELS_SHORT, plantafelHourSpan } from '../../utils/plantafelSettings';
import type { PlantafelDisplaySettings } from '../../utils/plantafelSettings';
import { isAbsoluteDayVisible, visibleColumnCount } from '../../utils/plantafelVisibleDays';
import type { PlantafelCreateDraft } from './plantafelTypes';
import { usePlantafelBlockResize } from './usePlantafelBlockResize';
import { usePlantafelCreateDrag } from './usePlantafelCreateDrag';
import type {
  AbsenceFilterKind,
  BoardActivity,
  BoardEmployee,
  GridBlock,
  PlantafelViewMode,
} from './plantafelTypes';
import {
  plantafelTypeFilterAbsenceId,
  plantafelTypeFilterActivityId,
} from './plantafelTypes';
import { usePlantafelBlockTooltip } from './plantafelBlockTooltip';
import {
  DAY_HEADER_H,
  PLANTAFEL_ROW_BASE_H,
  PROJECT_HEADER_ROW_H,
  RESOURCE_COL_W,
  SECTION_HEAD_H,
} from './plantafelTypes';
import {
  getTodayLeftPct,
  getViewSpan,
  gridBlockAssignedToEmployee,
  gridBlockScheduledHours,
} from './mapActivityToBlock';
import { dayIndexInWallClockViewport, formatWallClockYmd } from '../../utils/activityDateTime';
import {
  addDays,
  isoWeekNumber,
  localDateKey,
  rowCapacityHours,
  startOfDay,
  isWeekend,
  type MonthViewportMeta,
} from './plantafelViewport';
import { comparePlantafelEmployees } from '../../utils/plantafelEmployeeOrder';
import { employeeIdsWithVisibleBlocks } from './plantafelEmployeeVisibility';

function columnDayStyle(
  dateKey: string,
  publicHolidayByDate: Map<string, string>,
  opts: { today: boolean; weekend?: boolean },
): React.CSSProperties | undefined {
  if (opts.today) {
    return {
      background: 'color-mix(in srgb, var(--color-blue-500) 10%, var(--color-app-surface))',
    };
  }
  if (publicHolidayByDate.has(dateKey)) {
    return {
      background: 'color-mix(in srgb, var(--color-emerald-500) 10%, var(--color-app-surface))',
    };
  }
  if (opts.weekend) {
    return {
      background: 'color-mix(in srgb, var(--color-app-surface-muted) 72%, var(--color-app-surface))',
    };
  }
  return undefined;
}

function dayHeaderCellBg(opts: { today?: boolean; holiday?: boolean; weekend?: boolean }): string {
  if (opts.today) return 'bg-blue-50';
  if (opts.holiday) return 'bg-emerald-50';
  if (opts.weekend) return 'bg-app-surface-muted/90';
  return '';
}
import { PLANTAFEL_ORPHAN_LEAD_ID, type ProjectRow } from './plantafelTypes';
import { plantafelBlockLeadId } from './plantafelTypes';

type BlockColorStyle = {
  bg: string;
  border: string;
  text: string;
  /** Schraffurfarbe geplanter Termine; ohne Wert bleibt der Balken einfarbig. */
  stripe?: string;
  accentLeft?: string;
};

function blockColors(block: GridBlock): BlockColorStyle {
  if (block.isAbsence) {
    const a = absenceBarStyle(block.isAbsence);
    return {
      bg: a.bg,
      border: a.border,
      text: a.text,
      accentLeft: a.accent,
    };
  }
  const at = activityTypeBarStyle(block.activityType);
  const planned = block.status === 'geplant';
  return { bg: at.bg, border: at.border, text: at.text, stripe: planned ? at.stripe : undefined };
}

function EmployeeAvatar({ color, initials, size = 22 }: { color: string; initials: string; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold leading-none text-white"
      style={{ width: size, height: size, background: color, fontSize: size <= 22 ? 10 : 11 }}
    >
      {initials}
    </span>
  );
}

function AvatarStack({ employeeIds, employees, size = 18, max = 3 }: {
  employeeIds: string[];
  employees: BoardEmployee[];
  size?: number;
  max?: number;
}) {
  const list = employeeIds.slice(0, max).map((id) => employees.find((e) => e.id === id)).filter(Boolean) as BoardEmployee[];
  const extra = employeeIds.length - list.length;
  return (
    <span className="inline-flex items-center leading-none">
      {list.map((m, i) => (
        <span key={m.id} className={`inline-flex items-center ${i === 0 ? '' : '-ml-1.5'}`}>
          <EmployeeAvatar color={m.color} initials={m.initials} size={size} />
        </span>
      ))}
      {extra > 0 && (
        <span
          className="-ml-1.5 inline-flex items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-app-text-muted ring-2 ring-white"
          style={{ width: size, height: size }}
        >
          +{extra}
        </span>
      )}
    </span>
  );
}

export function EventBlock({
  block,
  viewMode,
  selected,
  onClick,
  employees,
  monthDayCount,
  viewportStart,
  activity,
  onActivityTimeChange,
  stackLayout,
  hideAssigneesOnBlock = false,
  timeZone,
}: {
  block: GridBlock;
  viewMode: PlantafelViewMode;
  selected: boolean;
  onClick: () => void;
  employees: BoardEmployee[];
  monthDayCount?: number;
  viewportStart: Date;
  activity?: BoardActivity;
  onActivityTimeChange?: (activityId: string, due_at: string, end_at: string) => void;
  stackLayout?: BlockStackLayout;
  /** Kein Kunden-/Mitarbeiter-Zweitext auf dem Balken (z. B. Unterzeile unter Projekt). */
  hideAssigneesOnBlock?: boolean;
  timeZone: string;
}) {
  const timelineRef = React.useContext(PlantafelTimelineRowRefContext);
  const { visibleWeekdays, dayHours } = usePlantafelDisplay();
  const isPending = Boolean(block.isPendingCreate);
  const resizable = !isPending && canResizeActivityBlock(block, viewMode);
  const {
    displayBlock,
    onResizePointerDown,
    onResizePointerMove,
    onResizePointerUp,
    isResizing,
    consumeSuppressClick,
  } = usePlantafelBlockResize({
    block,
    viewMode,
    viewportStart,
    activityId: block.activityId || undefined,
    timelineRef,
    onTimeChange: onActivityTimeChange,
    enabled: resizable,
    timeZone,
  });

  const span = getViewSpan(displayBlock, viewMode, monthDayCount, visibleWeekdays, dayHours);
  if (!span) return null;
  const col = blockColors(displayBlock);
  const isMonat = viewMode === 'monat';
  const isNarrow = span.widthPct < 6;
  const blockStripe = col.stripe
    ? `repeating-linear-gradient(45deg, transparent, transparent 6px, ${col.stripe} 6px, ${col.stripe} 12px)`
    : undefined;
  const { ref, show, hide, tooltip } = usePlantafelBlockTooltip(
    block,
    employees,
    viewportStart,
    viewMode,
    activity,
    timeZone,
  );
  const showTimes = !block.isAbsence;
  const timeLabel = formatBlockScheduleLabel(viewportStart, displayBlock, timeZone);
  const absenceDateLabel = block.isAbsence
    ? formatBlockAbsenceDateLabel(viewportStart, displayBlock, timeZone)
    : null;
  const stackPos = blockStackPositionStyle(stackLayout);
  const stacked = (stackLayout?.laneCount ?? 1) > 1;

  return (
    <>
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      data-activity-block="true"
      onClick={(e) => {
        e.stopPropagation();
        if (consumeSuppressClick()) return;
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      className={`pointer-events-auto absolute z-[4] flex cursor-pointer flex-col overflow-hidden rounded-md border py-1 ${
        resizable ? 'px-2.5' : 'px-2'
      } ${isResizing ? 'z-20' : ''}`}
      style={{
        left: `calc(${span.leftPct}% + 2px)`,
        width: `calc(${span.widthPct}% - 4px)`,
        ...stackPos,
        backgroundColor: col.bg,
        backgroundImage: blockStripe,
        borderColor: col.border,
        borderLeftWidth: col.accentLeft ? 4 : 1,
        borderLeftColor: col.accentLeft ?? col.border,
        borderStyle: isPending ? 'dashed' : 'solid',
        boxShadow: selected ? `0 0 0 2px ${col.accentLeft ?? col.border}` : '0 1px 2px rgba(0,0,0,.06)',
        opacity: isPending ? 0.92 : 1,
      }}
    >
      {resizable && (
        <>
          <div
            className="absolute bottom-0 left-0 top-0 z-10 w-2.5 cursor-ew-resize rounded-l-md hover:bg-black/5"
            title="Startzeit anpassen"
            onPointerDown={(e) => onResizePointerDown('start', e)}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="absolute bottom-0 right-0 top-0 z-10 w-2.5 cursor-ew-resize rounded-r-md hover:bg-black/5"
            title="Endzeit anpassen"
            onPointerDown={(e) => onResizePointerDown('end', e)}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      )}
      <div className={`flex min-h-0 flex-1 flex-col ${stacked ? 'gap-0' : 'gap-0.5'}`}>
        <div className="flex min-w-0 items-center gap-1">
          <span
            className="truncate text-xs font-semibold"
            style={{ color: col.text }}
          >
            {isPending ? 'Neuer Termin' : block.title}
          </span>
          {block.priority === 'dringend' && !isPending && (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
          )}
        </div>
        {absenceDateLabel && (
          <span
            className="truncate text-xs font-medium leading-tight opacity-90"
            style={{ color: col.text }}
          >
            {absenceDateLabel}
          </span>
        )}
        {!hideAssigneesOnBlock &&
          !block.isAbsence &&
          block.customer !== '—' &&
          !isNarrow &&
          !isMonat &&
          !stacked && (
            <div className="flex min-w-0 items-center gap-1">
              <span className="truncate text-xs opacity-85" style={{ color: col.text }}>
                {block.customer}
              </span>
              {block.assigneeEmployeeIds.length > 0 && (
                <AvatarStack employeeIds={block.assigneeEmployeeIds} employees={employees} size={16} />
              )}
            </div>
          )}
      </div>
      {showTimes && (
        <span
          className="relative z-[1] mt-auto flex min-w-0 shrink-0 items-center gap-0.5 text-xs font-medium tabular-nums leading-tight"
          style={{ color: col.text }}
        >
          <span className="truncate">{timeLabel}</span>
          {block.isRecurring ? (
            <Repeat className="h-2.5 w-2.5 shrink-0 opacity-90" strokeWidth={2.5} aria-label="Wiederholender Termin" />
          ) : null}
        </span>
      )}
    </div>
    {tooltip}
    </>
  );
}

function RowBackground({
  children,
  rowH,
  viewportStart,
  now,
  viewMode,
  publicHolidayByDate,
  monthDayCount,
  monthMeta,
  employeeId,
  leadId,
  onCreateRange,
  timeZone,
}: {
  children: React.ReactNode;
  rowH: number;
  viewportStart: Date;
  now: Date;
  viewMode: PlantafelViewMode;
  publicHolidayByDate: Map<string, string>;
  monthDayCount?: number;
  monthMeta?: MonthViewportMeta | null;
  employeeId?: string;
  leadId?: string | null;
  onCreateRange?: (draft: PlantafelCreateDraft) => void;
  timeZone: string;
}) {
  const { visibleWeekdays, dayHours } = usePlantafelDisplay();
  const rowRef = React.useRef<HTMLDivElement>(null);
  const hourSpan = plantafelHourSpan(dayHours);
  const createEnabled =
    Boolean(onCreateRange) && (viewMode === 'tag' || viewMode === 'woche' || viewMode === 'monat');
  const { previewPct, previewLabel, onPointerDown: onCreatePointerDown } = usePlantafelCreateDrag({
    enabled: createEnabled,
    viewMode,
    viewportStart,
    timelineRef: rowRef,
    employeeId,
    leadId,
    monthDayCount,
    onComplete: onCreateRange,
    timeZone,
  });
  const todayLeft = getTodayLeftPct(
    viewportStart,
    now,
    viewMode,
    monthDayCount,
    visibleWeekdays,
    dayHours,
    timeZone,
  );
  const todayIdx = dayIndexInWallClockViewport(
    viewportStart,
    now,
    viewMode,
    timeZone,
    monthDayCount,
  );
  const monatCols = monthDayCount ?? 35;
  const monatWeekCount = Math.max(1, Math.ceil(monatCols / 7));
  const monatVisibleCols = visibleColumnCount(visibleWeekdays, monatWeekCount);

  let columns: React.ReactNode;
  if (viewMode === 'tag') {
    const tagDayKey = localDateKey(viewportStart);
    // In der Tagesansicht füllt „heute" die ganze Fläche — die rote Jetzt-Linie reicht als Markierung.
    const tagColStyle = columnDayStyle(tagDayKey, publicHolidayByDate, {
      today: false,
      weekend: isWeekend(viewportStart),
    });
    columns = (
      <div
        className="pointer-events-none absolute inset-0 grid"
        style={{
          gridTemplateColumns: `repeat(${hourSpan}, 1fr)`,
          ...tagColStyle,
        }}
      >
        {Array.from({ length: hourSpan }).map((_, i) => (
          <div key={i} className="border-l border-app-border/60 first:border-l-0" />
        ))}
      </div>
    );
  } else if (viewMode === 'monat') {
    columns = (
      <div
        className="pointer-events-none absolute inset-0 grid"
        style={{ gridTemplateColumns: `repeat(${monatVisibleCols}, 1fr)` }}
      >
        {Array.from({ length: monatCols }, (_, i) => i)
          .filter((i) => isAbsoluteDayVisible(i, visibleWeekdays))
          .map((i) => {
            const dayInWeek = visibleWeekdays.indexOf(i % 7);
            const weekBoundary = dayInWeek === 0;
            const dateKey = localDateKey(addDays(viewportStart, i));
            const d = addDays(viewportStart, i);
            const outsideMonth =
              monthMeta != null &&
              (d.getMonth() !== monthMeta.focusMonth || d.getFullYear() !== monthMeta.focusYear);
            return (
              <div
                key={i}
                className={`border-l first:border-l-0 ${
                  weekBoundary ? 'border-app-border' : 'border-app-border/60'
                } ${outsideMonth && !isWeekend(d) ? 'bg-app-surface-muted/80' : ''}`}
                style={columnDayStyle(dateKey, publicHolidayByDate, {
                  today: i === todayIdx,
                  weekend: !outsideMonth && isWeekend(d),
                })}
              />
            );
          })}
      </div>
    );
  } else {
    columns = (
      <div
        className="pointer-events-none absolute inset-0 grid"
        style={{ gridTemplateColumns: `repeat(${visibleWeekdays.length}, 1fr)` }}
      >
        {visibleWeekdays.map((dow) => {
          const d = addDays(viewportStart, dow);
          const dateKey = localDateKey(d);
          return (
            <div
              key={dow}
              className="border-l border-app-border/60 first:border-l-0"
              style={columnDayStyle(dateKey, publicHolidayByDate, {
                today: dow === todayIdx,
                weekend: isWeekend(d),
              })}
            />
          );
        })}
      </div>
    );
  }

  return (
    <PlantafelTimelineRowRefContext.Provider value={rowRef}>
      {/* Zeilenlinie als Innenschatten statt border-b: so liegt die rote Jetzt-Linie darüber. Ein echter Rahmen bliebe wegen overflow-hidden unerreichbar und ließe helle Lücken. */}
      <div
        ref={rowRef}
        className={`relative overflow-hidden shadow-[inset_0_-1px_0_color-mix(in_srgb,var(--color-app-border)_60%,transparent)] ${createEnabled ? 'cursor-crosshair' : ''}`}
        style={{ height: rowH, touchAction: createEnabled ? 'none' : undefined }}
        onPointerDown={createEnabled ? onCreatePointerDown : undefined}
      >
        {columns}
        {todayLeft >= 0 && (
          <div
            className="pointer-events-none absolute top-0 bottom-0 z-[5] w-px bg-red-500"
            style={{ left: `${todayLeft}%` }}
          />
        )}
        {previewPct && previewPct.widthPct > 0 && (
          <div
            className="pointer-events-none absolute z-[3] flex flex-col justify-end overflow-hidden rounded-md border-2 border-dashed border-blue-500 bg-blue-100/60 px-1.5 py-1"
            style={{
              left: `calc(${previewPct.leftPct}% + 2px)`,
              width: `calc(${Math.max(previewPct.widthPct, 0.35)}% - 4px)`,
              top: 3,
              bottom: 3,
            }}
          >
            {previewLabel && (
              <span className="truncate text-xs font-medium tabular-nums text-blue-800">
                {previewLabel}
              </span>
            )}
          </div>
        )}
        {children}
      </div>
    </PlantafelTimelineRowRefContext.Provider>
  );
}

function HeaderCorner() {
  return (
    <div className="border-b border-r border-app-border" style={{ height: DAY_HEADER_H }} aria-hidden />
  );
}

function WocheCorner({ viewportStart }: { viewportStart: Date }) {
  const kw = isoWeekNumber(viewportStart);
  const weekEnd = addDays(viewportStart, 6);
  const fromLabel = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' }).format(viewportStart);
  const toLabel = new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(weekEnd);
  return (
    <div
      className="flex flex-col justify-center border-b border-r border-app-border bg-app-surface px-3.5"
      style={{ height: DAY_HEADER_H }}
    >
      <span className="text-xs font-medium text-app-text-subtle">Kalenderwoche</span>
      <span className="mt-0.5 text-sm font-semibold text-app-text">KW {kw}</span>
      <span className="text-xs text-app-text-subtle">
        {fromLabel}. – {toLabel}
      </span>
    </div>
  );
}

function WocheHeader({
  viewportStart,
  now,
  publicHolidayByDate,
  visibleWeekdays,
  timeZone,
}: {
  viewportStart: Date;
  now: Date;
  publicHolidayByDate: Map<string, string>;
  visibleWeekdays: number[];
  timeZone: string;
}) {
  const todayKey = formatWallClockYmd(now, timeZone);
  const isToday = (i: number) => {
    const d = addDays(viewportStart, i);
    return formatWallClockYmd(d, timeZone) === todayKey;
  };
  return (
    <>
      <WocheCorner viewportStart={viewportStart} />
      <div
        className="grid border-b border-app-border"
        style={{ height: DAY_HEADER_H, gridTemplateColumns: `repeat(${visibleWeekdays.length}, 1fr)` }}
      >
        {visibleWeekdays.map((dow) => {
          const d = addDays(viewportStart, dow);
          const wd = PLANTAFEL_WEEKDAY_LABELS_SHORT[dow];
          const today = isToday(dow);
          const monthShort = new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(d);
          const holidayName = publicHolidayByDate.get(localDateKey(d));
          const wknd = isWeekend(d);
          return (
            <div
              key={dow}
              className={`flex min-w-0 flex-col justify-center gap-0.5 border-l border-app-border/60 px-2.5 py-2 first:border-l-0 ${dayHeaderCellBg({
                today,
                holiday: Boolean(holidayName),
                weekend: wknd,
              })}`}
            >
              <span className="text-xs font-semibold tracking-wide text-app-text-subtle">{wd}</span>
              <div className="flex min-w-0 items-baseline gap-1.5 overflow-hidden">
                <span className={`shrink-0 text-lg font-semibold ${today ? 'text-blue-600' : 'text-app-text'}`}>
                  {d.getDate()}
                </span>
                <span className="shrink-0 text-xs text-app-text-subtle">{monthShort}</span>
                {holidayName && (
                  <span
                    className="min-w-0 truncate text-xs font-medium text-emerald-700"
                    title={holidayName}
                  >
                    {holidayName}
                  </span>
                )}
                {today && (
                  <span className="ml-auto shrink-0 rounded bg-blue-600 px-1 py-0.5 text-xs font-semibold text-white">
                    Heute
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function TagHeader({
  viewportStart,
  publicHolidayByDate,
}: {
  viewportStart: Date;
  publicHolidayByDate: Map<string, string>;
}) {
  const { dayHours } = usePlantafelDisplay();
  const hourSpan = plantafelHourSpan(dayHours);
  const hours: number[] = [];
  for (let h = dayHours.hourStart; h < dayHours.hourEnd; h++) hours.push(h);
  const dateLine = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(viewportStart);
  const holidayName = publicHolidayByDate.get(localDateKey(viewportStart));
  const tagWknd = isWeekend(viewportStart);

  return (
    <>
      <div
        className={`flex flex-col justify-center border-b border-r border-app-border px-4 ${dayHeaderCellBg({
          holiday: Boolean(holidayName),
          weekend: tagWknd && !holidayName,
        })}`}
        style={{ height: DAY_HEADER_H }}
      >
        <span className="text-xs font-medium text-app-text-subtle">Heute</span>
        <div className="mt-0.5 flex min-w-0 items-baseline gap-2 overflow-hidden">
          <span className="shrink-0 text-sm font-semibold capitalize text-app-text">{dateLine}</span>
          {holidayName && (
            <span className="min-w-0 truncate text-xs font-medium text-emerald-700" title={holidayName}>
              {holidayName}
            </span>
          )}
        </div>
      </div>
      <div
        className="relative grid border-b border-app-border"
        style={{ gridTemplateColumns: `repeat(${hourSpan}, 1fr)`, height: DAY_HEADER_H }}
      >
        {hours.map((h) => (
          <div
            key={h}
            className="flex flex-col justify-end border-l border-app-border/60 px-0.5 py-1.5 first:border-l-0"
          >
            <span className="text-center text-xs font-medium text-app-text-subtle">{h}:00</span>
          </div>
        ))}
      </div>
    </>
  );
}

function MonatCorner({ monthMeta }: { monthMeta: MonthViewportMeta }) {
  const monthLabel = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(
    new Date(monthMeta.focusYear, monthMeta.focusMonth, 1),
  );
  const firstKw = isoWeekNumber(monthMeta.viewportStart);
  const lastKw = isoWeekNumber(addDays(monthMeta.viewportStart, monthMeta.dayCount - 1));
  return (
    <div
      className="flex border-b border-r border-app-border bg-app-surface"
      style={{ height: DAY_HEADER_H }}
    >
      <div className="flex min-w-0 flex-1 flex-col justify-center px-3.5">
        <span className="text-xs font-medium text-app-text-subtle">Monat</span>
        <span className="mt-0.5 text-sm font-semibold capitalize text-app-text">{monthLabel}</span>
        <span className="text-xs text-app-text-subtle">
          KW {firstKw}
          {lastKw !== firstKw ? ` – ${lastKw}` : ''}
        </span>
      </div>
    </div>
  );
}

function MonatHeader({
  viewportStart,
  now,
  monthMeta,
  publicHolidayByDate,
  visibleWeekdays,
  timeZone,
}: {
  viewportStart: Date;
  now: Date;
  monthMeta: MonthViewportMeta;
  publicHolidayByDate: Map<string, string>;
  visibleWeekdays: number[];
  timeZone: string;
}) {
  const todayKey = formatWallClockYmd(now, timeZone);
  const weeks: { kw: number; monthLabel: string; days: Date[]; dataWeek: boolean }[] = [];
  for (let w = 0; w < monthMeta.weekCount; w++) {
    const weekStart = addDays(viewportStart, w * 7);
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const dataWeek = days.some(
      (d) => d.getMonth() === monthMeta.focusMonth && d.getFullYear() === monthMeta.focusYear,
    );
    weeks.push({
      kw: isoWeekNumber(weekStart),
      monthLabel: new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(weekStart),
      days,
      dataWeek,
    });
  }

  return (
    <>
      <MonatCorner monthMeta={monthMeta} />
      <div className="flex flex-col border-b border-app-border" style={{ height: DAY_HEADER_H }}>
          <div
            className="grid border-b border-app-border/60"
            style={{ height: 26, gridTemplateColumns: `repeat(${monthMeta.weekCount}, 1fr)` }}
          >
            {weeks.map((w) => (
              <div
                key={w.kw}
                className={`flex items-center gap-1.5 border-l border-app-border/60 px-2.5 first:border-l-0 ${
                  w.dataWeek ? 'bg-blue-50' : 'bg-app-surface'
                }`}
              >
                <span className={`text-xs font-semibold ${w.dataWeek ? 'text-blue-800' : 'text-app-text-muted'}`}>
                  KW {w.kw}
                </span>
                <span className={`text-xs ${w.dataWeek ? 'text-blue-600' : 'text-app-text-subtle'}`}>{w.monthLabel}</span>
              </div>
            ))}
          </div>
          <div
            className="grid flex-1"
            style={{
              gridTemplateColumns: `repeat(${visibleColumnCount(visibleWeekdays, monthMeta.weekCount)}, 1fr)`,
            }}
          >
            {weeks.flatMap((w, wi) =>
              visibleWeekdays.map((dow, vi) => {
                const d = w.days[dow];
                const overallIdx = wi * 7 + dow;
                const isToday = formatWallClockYmd(d, timeZone) === todayKey;
                const outsideMonth =
                  d.getMonth() !== monthMeta.focusMonth || d.getFullYear() !== monthMeta.focusYear;
                const isHoliday = !outsideMonth && publicHolidayByDate.has(localDateKey(d));
                const wknd = isWeekend(d);
                return (
                  <div
                    key={overallIdx}
                    className={`border-l py-1 text-center text-xs font-medium first:border-l-0 ${
                      vi === 0 ? 'border-app-border' : 'border-app-border/60'
                    } ${
                      isToday
                        ? 'bg-blue-600 text-white'
                        : isHoliday
                          ? 'bg-emerald-50 text-emerald-800'
                          : outsideMonth
                            ? 'bg-app-surface-muted/90 text-app-text-subtle'
                            : wknd
                              ? 'bg-app-surface-muted/90 text-app-text-subtle'
                              : 'text-app-text-muted'
                    }`}
                    title={isHoliday ? publicHolidayByDate.get(localDateKey(d)) : undefined}
                  >
                    {d.getDate()}
                  </div>
                );
              }),
            )}
          </div>
      </div>
    </>
  );
}

export function DayHeaderRow({
  viewportStart,
  now,
  viewMode,
  publicHolidayByDate,
  monthMeta,
  visibleWeekdays,
  timeZone,
}: {
  viewportStart: Date;
  now: Date;
  viewMode: PlantafelViewMode;
  publicHolidayByDate: Map<string, string>;
  monthMeta?: MonthViewportMeta | null;
  visibleWeekdays: number[];
  timeZone: string;
}) {
  return (
    <div
      className="sticky top-0 z-30 grid bg-app-surface"
      style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}
    >
      {viewMode === 'tag' && (
        <TagHeader viewportStart={viewportStart} publicHolidayByDate={publicHolidayByDate} />
      )}
      {viewMode === 'monat' && monthMeta && (
        <MonatHeader
          viewportStart={viewportStart}
          now={now}
          monthMeta={monthMeta}
          publicHolidayByDate={publicHolidayByDate}
          visibleWeekdays={visibleWeekdays}
          timeZone={timeZone}
        />
      )}
      {viewMode === 'monat' && !monthMeta && <HeaderCorner />}
      {viewMode === 'woche' && (
        <WocheHeader
          viewportStart={viewportStart}
          now={now}
          publicHolidayByDate={publicHolidayByDate}
          visibleWeekdays={visibleWeekdays}
          timeZone={timeZone}
        />
      )}
    </div>
  );
}

export function ResourceCell({
  employee,
  weekHours,
  capacity,
  rowH = PLANTAFEL_ROW_BASE_H,
  instancePrefix,
}: {
  employee: BoardEmployee;
  weekHours: number;
  capacity: number;
  rowH?: number;
  instancePrefix: string;
}) {
  const pct = capacity > 0 ? weekHours / capacity : 0;
  const barColor = pct > 1 ? '#ef4444' : pct > 0.9 ? '#f59e0b' : '#22c55e';
  const content = (
    <>
      <EmployeeAvatar color={employee.color} initials={employee.initials} size={28} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-app-text">{employee.name}</div>
        <div className="mt-0.5 truncate text-xs text-app-text-subtle" title={employee.role || undefined}>
          {employee.role}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-xs font-semibold tabular-nums" style={{ color: barColor }}>
          {Math.round(weekHours)}h/{Math.round(capacity)}h
        </span>
        <div className="relative h-0.5 w-14 overflow-hidden rounded bg-app-surface-muted">
          <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${Math.min(pct, 1) * 100}%`, background: barColor }} />
        </div>
      </div>
    </>
  );
  // Feste Zeilenhöhe: überlanger Inhalt wird geschnitten, statt in die Nachbarzeile zu laufen.
  const cellClass =
    'flex items-center gap-2.5 overflow-hidden border-b border-r border-app-border/60 bg-app-surface px-3.5';
  const { canOpen } = useModuleAccess();
  const profilePath = employeeDetailPathFromPrefix(instancePrefix, employee.id, 'profile');
  if (!employee.id || !canOpen(profilePath)) {
    return (
      <div className={cellClass} style={{ height: rowH }}>
        {content}
      </div>
    );
  }
  return (
    <Link
      to={profilePath}
      className={`${cellClass} cursor-pointer transition-colors hover:bg-app-hover`}
      style={{ height: rowH }}
      title={`${employee.name} – Profil öffnen`}
    >
      {content}
    </Link>
  );
}

function projectAssigneeNames(ids: string[], employees: BoardEmployee[], maxNames = 2): string {
  const names = ids
    .map((id) => employees.find((e) => e.id === id)?.name?.trim())
    .filter(Boolean) as string[];
  if (names.length === 0) return '';
  if (names.length <= maxNames) return names.join(', ');
  return `${names.slice(0, maxNames).join(', ')} +${names.length - maxNames}`;
}

export function ProjectCell({
  proj,
  rowH = PLANTAFEL_ROW_BASE_H,
  employees,
  assigneeEmployeeIds,
  variant = 'full',
}: {
  proj: ProjectRow;
  rowH?: number;
  employees?: BoardEmployee[];
  /** Mitarbeiter mit Terminen im aktuellen Viewport; fallback: proj.assigneeEmployeeIds */
  assigneeEmployeeIds?: string[];
  /** header = nur Projektinfo (Gruppierung nach Projekt); full = inkl. Einsatzteam */
  variant?: 'header' | 'full';
}) {
  const teamIds = assigneeEmployeeIds ?? proj.assigneeEmployeeIds;
  const teamLabel = employees ? projectAssigneeNames(teamIds, employees) : '';
  const showTeam = variant === 'full' && employees;
  const { canOpen } = useModuleAccess();
  const projectPath = leadDetailPath({ id: proj.leadId });
  const canOpenProject = proj.leadId !== PLANTAFEL_ORPHAN_LEAD_ID && canOpen(projectPath);
  const content = (
    <>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 leading-tight">
        <div className="truncate text-sm font-semibold text-app-text">{proj.title}</div>
        <div className="truncate text-xs text-app-text-subtle">{proj.customer}</div>
        {showTeam &&
          (teamIds.length > 0 ? (
            <div className="flex min-w-0 items-center gap-1.5" title={teamLabel}>
              <AvatarStack employeeIds={teamIds} employees={employees} size={14} max={4} />
              <span className="truncate text-xs font-medium leading-none text-app-text-muted">{teamLabel}</span>
            </div>
          ) : (
            <div className="text-xs leading-none text-app-text-subtle">Kein Einsatzteam</div>
          ))}
      </div>
    </>
  );
  const cellClass = 'flex items-center gap-2.5 border-b border-r border-app-border/60 bg-app-surface px-3.5 py-2';
  if (!canOpenProject) {
    return (
      <div className={cellClass} style={{ height: rowH }}>
        {content}
      </div>
    );
  }
  return (
    <Link
      to={projectPath}
      className={`${cellClass} cursor-pointer transition-colors hover:bg-app-hover`}
      style={{ height: rowH }}
      title={`${proj.title} – Projekt öffnen`}
    >
      {content}
    </Link>
  );
}

/** Mitarbeiter-Zeile unter einem Projekt (Gruppierung nach Projekt). */
export function ProjectEmployeeCell({
  employee,
  rowH = PLANTAFEL_ROW_BASE_H,
  instancePrefix,
}: {
  employee: BoardEmployee;
  rowH?: number;
  instancePrefix: string;
}) {
  const content = (
    <>
      <EmployeeAvatar color={employee.color} initials={employee.initials} size={24} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-app-text">{employee.name}</div>
        {employee.role ? (
          <div className="truncate text-xs text-app-text-subtle">{employee.role}</div>
        ) : null}
      </div>
    </>
  );
  const cellClass = 'flex items-center gap-2 border-b border-r border-app-border/60 bg-app-surface pl-9 pr-3.5';
  const { canOpen } = useModuleAccess();
  const profilePath = employeeDetailPathFromPrefix(instancePrefix, employee.id, 'profile');
  if (!employee.id || !canOpen(profilePath)) {
    return (
      <div className={cellClass} style={{ height: rowH }}>
        {content}
      </div>
    );
  }
  return (
    <Link
      to={profilePath}
      className={`${cellClass} cursor-pointer transition-colors hover:bg-app-hover`}
      style={{ height: rowH }}
      title={`${employee.name} – Profil öffnen`}
    >
      {content}
    </Link>
  );
}

function ProjectUnassignedCell({ rowH = PLANTAFEL_ROW_BASE_H }: { rowH?: number }) {
  return (
    <div
      className="flex items-center gap-2 border-b border-r border-app-border/60 bg-app-surface pl-9 pr-3.5"
      style={{ height: rowH }}
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-app-text-muted">
        ?
      </span>
      <div className="truncate text-xs font-medium text-app-text-subtle">Ohne Mitarbeiter-Zuweisung</div>
    </div>
  );
}

export function SectionHeader({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="grid w-full cursor-pointer select-none text-left"
      style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}
      onClick={onToggle}
    >
      <div
        className="flex items-center gap-1.5 border-b border-r border-app-border/60 bg-app-surface-muted px-3.5 text-xs font-medium text-app-text-muted"
        style={{ height: SECTION_HEAD_H }}
      >
        <span>{label}</span>
        <span className="font-normal text-app-text-subtle">{count}</span>
        {/* Lucide statt Textglyphe: `⌄` sitzt in der Zeile zu hoch und lässt sich nicht zentrieren.
            `-mr-1` gleicht den Leerraum im Icon-Viewbox aus, damit die Kante optisch auf den
            Stundenwerten der Zeilen darunter sitzt und nicht daneben. */}
        <ChevronDown
          className={`-mr-1 ml-auto h-4 w-4 shrink-0 text-app-text-subtle transition-transform ${
            collapsed ? '-rotate-90' : ''
          }`}
          aria-hidden
        />
      </div>
      <div className="border-b border-app-border bg-app-surface-muted" style={{ height: SECTION_HEAD_H }} />
    </button>
  );
}

export function PlantafelGridBody({
  viewportStart,
  now,
  viewMode,
  employees,
  activities,
  projects,
  blocks,
  groupBy,
  collapsed,
  onToggleSection,
  selectedBlockId,
  onSelectBlock,
  onActivityTimeChange,
  onCreateRange,
  filters,
  publicHolidayByDate,
  monthMeta,
  displaySettings,
  instancePrefix,
  timeZone,
}: {
  viewportStart: Date;
  now: Date;
  viewMode: PlantafelViewMode;
  employees: BoardEmployee[];
  activities: BoardActivity[];
  projects: ProjectRow[];
  blocks: GridBlock[];
  groupBy: 'mitarbeiter' | 'projekt';
  collapsed: { mitarbeiter: boolean; projekte: boolean };
  onToggleSection: (key: 'mitarbeiter' | 'projekte') => void;
  selectedBlockId: string | null;
  onSelectBlock: (id: string | null) => void;
  onActivityTimeChange?: (activityId: string, due_at: string, end_at: string) => void;
  onCreateRange?: (draft: PlantafelCreateDraft) => void;
  filters: {
    mitarbeiter: string[];
    projekt: string[];
    typ: string[];
    status: string[];
  };
  publicHolidayByDate: Map<string, string>;
  monthMeta?: MonthViewportMeta | null;
  displaySettings: PlantafelDisplaySettings;
  instancePrefix: string;
  timeZone: string;
}) {
  const { visibleWeekdays, dayHours } = displaySettings;
  const monthDayCount = monthMeta?.dayCount;
  const activityById = React.useMemo(
    () => new Map(activities.map((a) => [a.id, a])),
    [activities],
  );
  const blockMatchesProject = (b: GridBlock, projectLeadId: string) => {
    if (b.isAbsence) return false;
    return plantafelBlockLeadId(b.leadId) === projectLeadId;
  };

  const projectHasVisibleBlocks = (p: ProjectRow) =>
    visible.some((b) => blockMatchesProject(b, p.leadId));

  const matchesEmployee = (b: GridBlock) => {
    if (isFilterMultiSelectNone(filters.mitarbeiter)) return false;
    if (!filters.mitarbeiter.length) return true;
    const ids = [
      ...(b.primaryEmployeeId ? [b.primaryEmployeeId] : []),
      ...b.assigneeEmployeeIds,
    ];
    return ids.some((id) => filters.mitarbeiter.includes(id));
  };

  const matchesProjectBlock = (b: GridBlock) => {
    if (isFilterMultiSelectNone(filters.projekt)) return false;
    if (!filters.projekt.length) return true;
    // Absences are not tied to a project — keep them visible when filtering activities by project
    // (same idea as matchesStatusFilter). Feiertage remain controllable via the type filter.
    if (b.isAbsence) return true;
    return filters.projekt.includes(plantafelBlockLeadId(b.leadId));
  };

  const matchesTypeFilter = (b: GridBlock) => {
    if (isFilterMultiSelectNone(filters.typ)) return false;
    if (!filters.typ.length) return true;

    if (b.isAbsence) {
      if (b.isAbsence === 'feiertag') return false;
      return filters.typ.includes(plantafelTypeFilterAbsenceId(b.isAbsence as AbsenceFilterKind));
    }
    return filters.typ.includes(plantafelTypeFilterActivityId(b.activityType));
  };

  const matchesStatusFilter = (b: GridBlock) => {
    if (isFilterMultiSelectNone(filters.status)) return false;
    if (!filters.status.length) return true;
    // Absences have no scheduling status — keep them visible when filtering activities by status.
    if (b.isAbsence) return true;
    return filters.status.includes(b.status);
  };

  const visible = blocks.filter(
    (b) =>
      matchesEmployee(b) &&
      matchesProjectBlock(b) &&
      matchesTypeFilter(b) &&
      matchesStatusFilter(b),
  );

  const employeeFilterActive = filters.mitarbeiter.length > 0;
  const typeFilterActive =
    filters.typ.length > 0 && !isFilterMultiSelectNone(filters.typ);

  let employeesToShow = employees;
  if (isFilterMultiSelectNone(filters.typ) || isFilterMultiSelectNone(filters.status)) {
    employeesToShow = [];
  }
  if (employeeFilterActive) {
    employeesToShow = employeesToShow.filter((e) => filters.mitarbeiter.includes(e.id));
  }
  // Type filter (activities + absences): hide employees without a matching entry in the viewport.
  if (typeFilterActive && employeesToShow.length > 0) {
    const withMatchingBlocks = employeeIdsWithVisibleBlocks(visible);
    employeesToShow = employeesToShow.filter((e) => withMatchingBlocks.has(e.id));
  }

  const projectsToShow = (
    filters.projekt.length
      ? projects.filter((p) => filters.projekt.includes(p.leadId))
      : projects
  ).filter((p) => (filters.projekt.length ? true : projectHasVisibleBlocks(p)));

  const blockOnEmployeeRow = (b: GridBlock, empId: string) =>
    gridBlockAssignedToEmployee(b, empId);

  const blockHasEmployeeAssignee = (b: GridBlock) =>
    Boolean(b.primaryEmployeeId || b.assigneeEmployeeIds.length);

  const blockOnProjectEmployeeRow = (b: GridBlock, empId: string, leadId: string) => {
    if (!blockMatchesProject(b, leadId)) return false;
    return gridBlockAssignedToEmployee(b, empId);
  };

  const employeeIdsForProjectBlocks = (leadBlocks: GridBlock[]) => {
    const ids = new Set<string>();
    for (const b of leadBlocks) {
      if (b.isAbsence) continue;
      if (b.primaryEmployeeId) ids.add(b.primaryEmployeeId);
      for (const id of b.assigneeEmployeeIds) ids.add(id);
    }
    return [...ids]
      .map((id) => employees.find((e) => e.id === id))
      .filter(Boolean)
      .sort(comparePlantafelEmployees) as BoardEmployee[];
  };

  const renderTimelineRow = (
    key: string,
    resource: React.ReactNode,
    rowBlocks: GridBlock[],
    opts: { employeeId?: string; leadId?: string | null; hideAssigneesOnBlock?: boolean },
  ) => {
    const { rowHeight, lanesByBlockId } = computeRowStackLayout(
      rowBlocks,
      viewMode,
      monthDayCount,
      visibleWeekdays,
      dayHours,
    );
    return (
      <div key={key} className="grid" style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}>
        {resource}
        <RowBackground
          rowH={rowHeight}
          viewportStart={viewportStart}
          now={now}
          viewMode={viewMode}
          publicHolidayByDate={publicHolidayByDate}
          monthDayCount={monthDayCount}
          monthMeta={monthMeta}
          employeeId={opts.employeeId}
          leadId={opts.leadId}
          onCreateRange={onCreateRange}
          timeZone={timeZone}
        >
          {rowBlocks.map((b) => (
            <EventBlock
              key={b.id}
              block={b}
              viewMode={viewMode}
              employees={employees}
              monthDayCount={monthDayCount}
              viewportStart={viewportStart}
              activity={b.activityId ? activityById.get(b.activityId) : undefined}
              selected={selectedBlockId === b.id}
              onClick={() => onSelectBlock(selectedBlockId === b.id ? null : b.id)}
              onActivityTimeChange={onActivityTimeChange}
              stackLayout={lanesByBlockId.get(b.id)}
              hideAssigneesOnBlock={opts.hideAssigneesOnBlock}
              timeZone={timeZone}
            />
          ))}
        </RowBackground>
      </div>
    );
  };

  const renderEmployeeRows = () =>
    employeesToShow.map((emp) => {
      const myBlocks = visible.filter((b) => blockOnEmployeeRow(b, emp.id));
      let weekHours = 0;
      for (const b of myBlocks) {
        weekHours += gridBlockScheduledHours(b, emp.expected_hours_per_day);
      }
      const capacity = rowCapacityHours(
        emp.expected_hours_per_day,
        emp.capacity_hours,
        viewMode,
        monthDayCount,
        visibleWeekdays,
      );
      const { rowHeight, lanesByBlockId } = computeRowStackLayout(
        myBlocks,
        viewMode,
        monthDayCount,
        visibleWeekdays,
        dayHours,
      );
      return (
        <div key={emp.id} className="grid" style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}>
          <ResourceCell employee={emp} weekHours={weekHours} capacity={capacity} rowH={rowHeight} instancePrefix={instancePrefix} />
          <RowBackground
            rowH={rowHeight}
            viewportStart={viewportStart}
            now={now}
            viewMode={viewMode}
            publicHolidayByDate={publicHolidayByDate}
            monthDayCount={monthDayCount}
            monthMeta={monthMeta}
            employeeId={emp.id}
            onCreateRange={onCreateRange}
            timeZone={timeZone}
          >
            {myBlocks.map((b) => (
              <EventBlock
                key={b.id}
                block={b}
                viewMode={viewMode}
                employees={employees}
                monthDayCount={monthDayCount}
                viewportStart={viewportStart}
                activity={b.activityId ? activityById.get(b.activityId) : undefined}
                selected={selectedBlockId === b.id}
                onClick={() => onSelectBlock(selectedBlockId === b.id ? null : b.id)}
                onActivityTimeChange={onActivityTimeChange}
                stackLayout={lanesByBlockId.get(b.id)}
                hideAssigneesOnBlock
                timeZone={timeZone}
              />
            ))}
          </RowBackground>
        </div>
      );
    });

  /** Gruppierung nach Mitarbeiter: eine Zeile pro Projekt inkl. Einsatzteam, Termine auf der Projektzeile. */
  const renderProjectRowsFlat = () =>
    projectsToShow.map((p) => {
      const leadBlocks = visible.filter((b) => blockMatchesProject(b, p.leadId));
      const { rowHeight, lanesByBlockId } = computeRowStackLayout(
        leadBlocks,
        viewMode,
        monthDayCount,
        visibleWeekdays,
        dayHours,
      );
      const teamIds = [
        ...new Set(
          leadBlocks.flatMap((b) => [
            ...(b.primaryEmployeeId ? [b.primaryEmployeeId] : []),
            ...b.assigneeEmployeeIds,
          ]),
        ),
      ];
      return (
        <div key={p.key} className="grid" style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}>
          <ProjectCell
            proj={p}
            rowH={rowHeight}
            employees={employees}
            assigneeEmployeeIds={teamIds.length > 0 ? teamIds : p.assigneeEmployeeIds}
            variant="full"
          />
          <RowBackground
            rowH={rowHeight}
            viewportStart={viewportStart}
            now={now}
            viewMode={viewMode}
            publicHolidayByDate={publicHolidayByDate}
            monthDayCount={monthDayCount}
            monthMeta={monthMeta}
            leadId={p.leadId}
            onCreateRange={onCreateRange}
            timeZone={timeZone}
          >
            {leadBlocks.map((b) => (
              <EventBlock
                key={b.id + '-p'}
                block={b}
                viewMode={viewMode}
                employees={employees}
                monthDayCount={monthDayCount}
                viewportStart={viewportStart}
                activity={b.activityId ? activityById.get(b.activityId) : undefined}
                selected={selectedBlockId === b.id}
                onClick={() => onSelectBlock(selectedBlockId === b.id ? null : b.id)}
                onActivityTimeChange={onActivityTimeChange}
                stackLayout={lanesByBlockId.get(b.id)}
                hideAssigneesOnBlock
                timeZone={timeZone}
              />
            ))}
          </RowBackground>
        </div>
      );
    });

  /** Gruppierung nach Projekt: Kopfzeile pro Projekt, darunter Mitarbeiter-Zeilen mit Terminen. */
  const renderProjectRowsNested = () =>
    projectsToShow.map((p) => {
      const leadBlocks = visible.filter((b) => blockMatchesProject(b, p.leadId));
      const projectEmployees = employeeIdsForProjectBlocks(leadBlocks);
      const unassigned = leadBlocks.filter((b) => !blockHasEmployeeAssignee(b));

      return (
        <React.Fragment key={p.key}>
          <div
            className="grid border-b border-app-border"
            style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}
          >
            <ProjectCell proj={p} rowH={PROJECT_HEADER_ROW_H} variant="header" />
            <div
              className="border-b border-app-border/60 bg-app-surface-muted/50"
              style={{ height: PROJECT_HEADER_ROW_H }}
            />
          </div>
          {projectEmployees.map((emp) => {
            const myBlocks = leadBlocks.filter((b) => blockOnProjectEmployeeRow(b, emp.id, p.leadId));
            return renderTimelineRow(
              `${p.key}-${emp.id}`,
              <ProjectEmployeeCell employee={emp} instancePrefix={instancePrefix} />,
              myBlocks,
              { employeeId: emp.id, leadId: p.leadId, hideAssigneesOnBlock: true },
            );
          })}
          {unassigned.length > 0 &&
            renderTimelineRow(
              `${p.key}-unassigned`,
              <ProjectUnassignedCell />,
              unassigned,
              { leadId: p.leadId },
            )}
        </React.Fragment>
      );
    });

  const sections =
    groupBy === 'projekt'
      ? [
          {
            key: 'projekte' as const,
            label: 'Projekte',
            count: projectsToShow.length,
            render: renderProjectRowsNested,
          },
        ]
      : [
          {
            key: 'mitarbeiter' as const,
            label: 'Mitarbeiter',
            count: employeesToShow.length,
            render: renderEmployeeRows,
          },
          {
            key: 'projekte' as const,
            label: 'Projekte',
            count: projectsToShow.length,
            render: renderProjectRowsFlat,
          },
        ];

  return (
    <PlantafelDisplayContext.Provider value={displaySettings}>
      <DayHeaderRow
        viewportStart={viewportStart}
        now={now}
        viewMode={viewMode}
        publicHolidayByDate={publicHolidayByDate}
        monthMeta={monthMeta}
        visibleWeekdays={visibleWeekdays}
        timeZone={timeZone}
      />
      {sections.map((s) => (
        <React.Fragment key={s.key}>
          <SectionHeader
            label={s.label}
            count={s.count}
            collapsed={collapsed[s.key]}
            onToggle={() => onToggleSection(s.key)}
          />
          {!collapsed[s.key] && s.render()}
        </React.Fragment>
      ))}
    </PlantafelDisplayContext.Provider>
  );
}
